import { Heading, HeadingLevel } from "@ariakit/react";
import { Failure, Success } from "@indietabletop/appkit/async-op";
import { useMediaQuery } from "@indietabletop/appkit/use-media-query";
import { useCallback, useMemo, useRef } from "react";
import { Link, Route, Switch, useLocation, useParams } from "wouter";
import { Army } from "../../domain/army";
import { Unit } from "../../domain/unit";
import {
  ActionsMenu,
  MenuDialogDisclosure,
  MenuDivider,
} from "../actions-menu";
import { UpdateArmyDialog } from "../army-dialog";
import { ArmyKeywordsDialog } from "../army-keywords-dialog";
import { atomic } from "../atomic.css";
import { ConfirmDialog } from "../confirm-dialog";
import { ArmyConstraintsValidationBox } from "../constraints-validation";
import { ContentView } from "../content-view";
import { useDatabase, useQuery } from "../database-provider";
import { DialogDisclosureButton } from "../dialog-buttons";
import { DialogTrigger } from "../dialog-trigger";
import { KnownErrorView } from "../error-view";
import { useFetchSharedArmy } from "../hooks/use-fetch-shared-army";
import { Icon } from "../icon";
import { IconButton } from "../icon-button";
import { KeywordItem } from "../keyword-item";
import { DialogListButton } from "../list-button";
import { ListEndMarker } from "../list-end-marker";
import { LoadingView } from "../loading-view";
import { MinWidth } from "../media";
import { Multiplier } from "../multiplier-counter";
import { Paragraphs } from "../paragraphs";
import { useRuleset } from "../ruleset-provider/context";
import { ShareArmyDialog } from "../share-army-dialog";
import { SubtleHeading } from "../subtle-heading";
import { CreateUnitDialog } from "../unit-dialog";
import { UnitView } from "../unit-view";
import { VerticalDivider } from "../vertical-divider";
import * as css from "./style.css";

function useArmyQuery() {
  const ruleset = useRuleset();
  const params = useParams<{ armyId: string }>();
  const armyOp = useQuery(
    useCallback((db) => db.getArmy(params.armyId), [params.armyId]),
    `army:${params.armyId}`,
  );

  return useMemo(
    () =>
      armyOp.flatMap((data) => {
        return data
          ? new Success(new Army(ruleset, data))
          : new Failure({ type: "ARMY_NOT_FOUND" as const });
      }),
    [armyOp, ruleset],
  );
}

function UnitListItem(props: { unit: Unit }) {
  const { unit } = props;
  const [location] = useLocation();

  const href = `/u/${unit.data.id}`;
  const isActive = href === location;

  return (
    <li key={unit.data.id} className={css.unitListItem}>
      <Link
        href={href}
        className={atomic({
          paddingBlockStart: 18,
          paddingBlockEnd: 16,
          paddingInlineStart: isActive ? 16 : { hover: 16, default: 0 },
          borderInlineStart: isActive ? "thick" : "none",
          display: "grid",
          gridTemplateColumns: "fraction-auto",
          gap: 12,
          alignItems: "center",
          transition: "all",
          backgroundColor: {
            hover: "background-secondary",
          },
        })}
      >
        <div>
          <div className={css.unitName}>
            {unit.name}
            {unit.data.count > 1 && (
              <>
                {" "}
                <Multiplier count={unit.data.count} />
              </>
            )}
          </div>

          <div
            className={css.unitSummary}
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {!unit.isValid && (
              <span
                className={atomic({
                  backgroundColor: "text",
                  color: "background-primary",
                  fontFamily: "arboria",
                  fontWeight: "black",
                  fontStyle: "italic",
                  textTransform: "uppercase",
                  paddingInline: 6,
                  fontSize: 12,
                  paddingBlockEnd: 2,
                  marginInlineEnd: 6,
                })}
              >
                Invalid
              </span>
            )}
            {unit.summary}
          </div>
        </div>

        <Icon name="forward" />
      </Link>
    </li>
  );
}

function UnitDetailView(props: {
  army: Army;
  headerIcon: "back" | "cross";
  readOnly?: boolean;
}) {
  const params = useParams<{ unitId: string }>();
  const unit = props.army.getUnit(params.unitId);

  if (!unit) {
    return (
      <HeadingLevel>
        <ContentView
          heading={!props.readOnly ? "Unit" : "Shared Unit"}
          backHref="/"
          icon={props.headerIcon}
        >
          <Heading
            className={atomic({
              fontFamily: "barlow-condensed",
              fontSize: 24,
              fontWeight: "semi-bold",
              marginBlockEnd: 8,
            })}
          >
            Unit not found
          </Heading>
          <p
            className={atomic({
              maxInlineSize: "optimal-reading",
              lineHeight: 1.5,
              fontSize: 16,
            })}
          >
            The link you followed might be broken, or the unit you were looking
            for was removed.
          </p>
        </ContentView>
      </HeadingLevel>
    );
  }

  return (
    <HeadingLevel>
      <UnitView {...props} unit={unit} />
    </HeadingLevel>
  );
}

function ArmyDetailView(props: { army: Army; readOnly?: boolean }) {
  const { army, readOnly } = props;

  const editButtonRef = useRef<HTMLDivElement | null>(null);
  const db = useDatabase();
  const [_, navigate] = useLocation();
  const ruleset = useRuleset();

  // In readOnly mode, we want to show the army keyword section only if the
  // keyword is present. In readWrite mode, we show it unconditionally, because
  // it allows the user to add the keyword.
  const showArmyKeywordSection = (readOnly && !!army.keyword) || !readOnly;

  return (
    <>
      <ContentView
        heading={!readOnly ? "Army" : "Shared Army"}
        backHref="~/army"
        icon="back"
        menu={
          !readOnly && (
            <ActionsMenu>
              <DialogTrigger>
                <UpdateArmyDialog army={army} />
                <MenuDialogDisclosure ref={editButtonRef}>
                  Edit
                </MenuDialogDisclosure>
              </DialogTrigger>

              <DialogTrigger>
                <ConfirmDialog
                  heading="Duplicate army?"
                  description={`A new army with identical units and keywords will be created.`}
                  submitLabel="Duplicate"
                  onSubmit={async () => {
                    const id = await db.cloneArmy(army.data, {
                      disambiguateName: true,
                    });
                    navigate(`~/army/a/${id}`);
                  }}
                />
                <MenuDialogDisclosure>Duplicate</MenuDialogDisclosure>
              </DialogTrigger>

              <DialogTrigger>
                <ShareArmyDialog army={army} />
                <MenuDialogDisclosure>Share</MenuDialogDisclosure>
              </DialogTrigger>

              <MenuDivider />

              <DialogTrigger>
                <ConfirmDialog
                  heading="Delete army?"
                  description={`Are you sure you want to delete "${army.name}"?`}
                  submitLabel="Delete"
                  onSubmit={async () => {
                    await db.deleteArmy(army.data);
                    navigate("~/army");
                  }}
                />
                <MenuDialogDisclosure>Delete</MenuDialogDisclosure>
              </DialogTrigger>
            </ActionsMenu>
          )
        }
      >
        <div className={css.armyDetailView}>
          <div
            className={atomic({
              display: "flex",
              flexDirection: "column",
              gap: 24,
            })}
          >
            <div className={css.armyHeader}>
              <Heading className={css.armyName}>
                <span onDoubleClick={() => editButtonRef.current?.click()}>
                  {army.name}
                </span>
              </Heading>
              <p className={css.armySummary}>{army.summary}</p>
            </div>

            {army.data.description && (
              <Paragraphs text={army.data.description} />
            )}

            {army.failingConditions.length > 0 && (
              <ArmyConstraintsValidationBox army={army} />
            )}
          </div>

          {showArmyKeywordSection && (
            <HeadingLevel>
              <div>
                <SubtleHeading>Army keyword</SubtleHeading>

                <div>
                  {army.keyword ? (
                    <KeywordItem
                      name={army.keyword.name}
                      summary={army.keyword.summary}
                      standalone
                      icon={
                        !readOnly && (
                          <IconButton
                            label="Remove army keyword"
                            icon="cross"
                            onClick={() => {
                              void db.updateArmy({
                                ...army.data,
                                ruleset_version: ruleset.version,
                                keywords: [],
                              });
                            }}
                          />
                        )
                      }
                    />
                  ) : !readOnly ? (
                    <DialogTrigger>
                      <ArmyKeywordsDialog {...props} />
                      <DialogListButton>Add keyword</DialogListButton>
                    </DialogTrigger>
                  ) : null}
                </div>
              </div>
            </HeadingLevel>
          )}

          <HeadingLevel>
            <div>
              <SubtleHeading>Units</SubtleHeading>
              <div>
                {army.units.length > 0 && (
                  <ul className={css.unitsList}>
                    {army.units.map((unit) => {
                      return <UnitListItem key={unit.data.id} unit={unit} />;
                    })}
                  </ul>
                )}

                {!readOnly ? (
                  <DialogTrigger>
                    <CreateUnitDialog {...props} />
                    <DialogListButton>Add unit</DialogListButton>
                  </DialogTrigger>
                ) : (
                  <ListEndMarker />
                )}
              </div>
            </div>
          </HeadingLevel>

          {readOnly && (
            <DialogTrigger>
              <ConfirmDialog
                heading="Copy shared army?"
                description={`${army.name} will be copied over to your armies.`}
                submitLabel="Copy over"
                onSubmit={async () => {
                  const id = await army.ruleset.db.cloneArmy(army.data);
                  navigate(`~/army/a/${id}`);
                }}
              />
              <DialogDisclosureButton>Copy shared army</DialogDisclosureButton>
            </DialogTrigger>
          )}
        </div>
      </ContentView>
    </>
  );
}

function ArmyView(props: { army: Army; readOnly?: boolean }) {
  const isWide = useMediaQuery(MinWidth.WIDE);

  if (!isWide) {
    return (
      <Switch>
        <Route path="/">
          <ArmyDetailView {...props} />
        </Route>
        <Route path="/u/:unitId">
          <UnitDetailView {...props} headerIcon="back" />
        </Route>
      </Switch>
    );
  }

  return (
    <HeadingLevel>
      <main className={css.view}>
        <div
          className={atomic({
            overflow: "auto",
            xScrollbarVisibility: "hidden",
          })}
        >
          <ArmyDetailView {...props} />
        </div>

        <div className={css.divider}>
          <VerticalDivider />
        </div>

        <div
          className={atomic({
            overflow: "auto",
            xScrollbarVisibility: "hidden",
          })}
        >
          <Switch>
            <Route path="/u/:unitId">
              <UnitDetailView {...props} headerIcon="cross" />
            </Route>

            <Route>
              <div className={css.illustrationWrap}>
                <div className={css.illustration} />
              </div>
            </Route>
          </Switch>
        </div>
      </main>
    </HeadingLevel>
  );
}

export function ArmyDetailPage() {
  const armyOp = useArmyQuery();

  if (armyOp.isPending) {
    return (
      <ContentView heading="Army" backHref="~/army" icon="back">
        <LoadingView />
      </ContentView>
    );
  }

  if (armyOp.isFailure) {
    return (
      <ContentView heading="Army" backHref="~/army" icon="back">
        <KnownErrorView code={armyOp.failure.type} />
      </ContentView>
    );
  }

  return <ArmyView army={armyOp.value} />;
}

export function SharedArmyDetailPage() {
  const ruleset = useRuleset();
  const { shareId } = useParams<{ shareId: string }>();
  const armyDataOp = useFetchSharedArmy(shareId);
  const armyOp = armyDataOp.mapSuccess((data) => new Army(ruleset, data));

  if (armyOp.isPending) {
    return (
      <ContentView heading="Shared Army" backHref="~/army" icon="back">
        <LoadingView />
      </ContentView>
    );
  }

  if (armyOp.isFailure) {
    return (
      <ContentView heading="Shared Army" backHref="~/army" icon="back">
        <KnownErrorView code={armyOp.failure.type} />
      </ContentView>
    );
  }

  return <ArmyView army={armyOp.value} readOnly />;
}
